import { useCallback } from "react"
import { GET_TIMETABLE, GET_TIMETABLE_CHANGES } from "@graphql"
import { NetworkStatus, useLazyQuery, useQuery } from "@apollo/client"

const useTimetable = (sessionId) => {
  const { data, loading, error, refetch, networkStatus } = useQuery(GET_TIMETABLE, {
    variables: { input: { id: sessionId } },
    skip: !sessionId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  })

  const isInitialLoad = loading && networkStatus === NetworkStatus.loading;
  const isRefetching = networkStatus === NetworkStatus.refetch;  

  const [getTimetableChanges, { loading: partialLoading, error: partialError }] = useLazyQuery(GET_TIMETABLE_CHANGES, {
    fetchPolicy: "network-only",
  })

  const fullRefetch = useCallback(() => {
    return refetch({ input: { id: sessionId } }).then((response) => response?.data?.sessionTimetable)
  }, [refetch, sessionId])

  const partialRefetch = (lastUpdatedAt) => {
    return getTimetableChanges({ 
      variables: {
        input: { 
          id: sessionId, 
          last_updated_at: lastUpdatedAt 
        } 
      }
    }).then((response) => response.data.sessionTimetableChanges)
  }

  /**
   * Skipped slots are those that were attempted to be updated but couldn't be because they have been booked in the meantime.
   */
  const updateTimetableAvailabilityCache = (cache, updatedSlots, skippedSlots) => {
    skippedSlots.forEach(skippedSlot => {
      cache.modify({
        id: cache.identify({ __typename: "SessionTimetableSlot", id: skippedSlot.id }),
        fields: { 
          status: () => "BOOKED",
          booking: () => {
            const [bookingSlot] = skippedSlot.bookingSlots ?? null
            
            if (!bookingSlot) return null;

            return {
              __typename: "SessionTimetableBooking",
              id: bookingSlot.booking_id,
              student_initials: bookingSlot.student.forename.charAt(0) + bookingSlot.student.surname.charAt(0),
              booking_slot_id: bookingSlot.id,
              student_id: bookingSlot.student.id,
            }
          }
        },
      })
    })
      
    updatedSlots.forEach(updatedSlot => {
      cache.modify({
        id: cache.identify({ __typename: "SessionTimetableSlot", id: updatedSlot.id }),
        fields: { status: () => updatedSlot.status.toUpperCase() },
      })
    })
  }

  return {
    timetable: data ? data.sessionTimetable : null,
    loading: isInitialLoad,
    isRefetching,
    error,
    fullRefetch,
    partialError,
    partialLoading,
    partialRefetch,
    updateTimetableAvailabilityCache,
  }
}

export default useTimetable